import { RoomType, isSpecialExteriorRoomType, type C1_OpeningData, type WallSide } from "../types/C1_ModelTypes";
import type { C1_Floor } from "../model/C1_Floor";
import type { C1_Room } from "../model/C1_Room";
import type { C1_Wall } from "../model/C1_Wall";

const DOOR_WIDTH = 0.9;
const HALLWAY_ENTRANCE_DOOR_WIDTH = 1.2;
const WINDOW_WIDTH = 1.2;
const DOOR_EDGE_DISTANCE = 0.1;
const TOLERANCE = 0.001;
type Point = { x: number; y: number };
type WallSegment = {
  room: C1_Room;
  wall: C1_Wall;
  start: Point;
  end: Point;
  ux: number;
  uy: number;
  length: number;
  side: WallSide;
  exterior: boolean;
};
type DoorConnection = { first: WallSegment; second: WallSegment; start: number; end: number };
export type C1_AutoOpening = C1_OpeningData & { roomId: string; _wallId: string };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function doorReflectForPlacement(side: WallSide, offset: number, currentReflect = false): boolean {
  const orientedOffset = side === "left" || side === "bottom" ? 1 - offset : offset;
  return doorReflectForWallOffset(orientedOffset, currentReflect);
}

/** Chooses the hinge from an offset measured in the concrete wall segment's own direction. */
export function doorReflectForWallOffset(offset: number, currentReflect = false): boolean {
  if (offset < 0.25) return true;
  if (offset > 0.75) return false;
  return currentReflect;
}

/** Pick the nearer wall end from the cursor, before the door centre is clamped or snapped. */
export function doorReflectForWallCursor(start: Point, end: Point, cursor: Point, currentReflect = false): boolean {
  const difference = (cursor.x - start.x) ** 2 + (cursor.y - start.y) ** 2
    - (cursor.x - end.x) ** 2 - (cursor.y - end.y) ** 2;
  if (Math.abs(difference) <= 1e-9) return currentReflect;
  return difference < 0;
}

/** Reads actual visible polygon edges; outdoor perimeter walls do not receive automatic openings. */
function floorWallSegments(floor: C1_Floor): WallSegment[] {
  const segments: WallSegment[] = [];
  for (const room of floor.rooms) {
    const enclosedGarage = room.type === RoomType.Garage || room.type === RoomType.DetachedGarage;
    if (isSpecialExteriorRoomType(room.type) && !enclosedGarage) continue;
    for (const wall of room.walls ?? []) {
      if (!wall.visible || wall.partnerWall?.visible === false) continue;
      const points = room.wallPoints(wall);
      if (!points) continue;
      const dx = points.end.x - points.start.x;
      const dy = points.end.y - points.start.y;
      const length = Math.hypot(dx, dy);
      if (length < TOLERANCE) continue;
      segments.push({
        room,
        wall,
        ...points,
        ux: dx / length,
        uy: dy / length,
        length,
        side: room.compatibilitySide(wall),
        exterior: room.isExteriorSegment(wall),
      });
    }
  }
  return segments;
}

/** Projects a world position onto the wall's own directed distance axis. */
function distanceOnWall(wall: WallSegment, point: Point): number {
  return (point.x - wall.start.x) * wall.ux + (point.y - wall.start.y) * wall.uy;
}

/** Finds a shared boundary long enough for a door, ignoring rectangle bounding boxes. */
function sharedWallConnection(first: WallSegment, second: WallSegment): DoorConnection | undefined {
  if (first.room === second.room || first.exterior || second.exterior) return undefined;
  if (first.ux * second.ux + first.uy * second.uy > -1 + TOLERANCE) return undefined;
  for (const point of [second.start, second.end]) {
    const distance = (point.x - first.start.x) * first.uy - (point.y - first.start.y) * first.ux;
    if (Math.abs(distance) > TOLERANCE) return undefined;
  }
  const secondStart = distanceOnWall(first, second.start);
  const secondEnd = distanceOnWall(first, second.end);
  const start = Math.max(0, Math.min(secondStart, secondEnd));
  const end = Math.min(first.length, Math.max(secondStart, secondEnd));
  if (end - start < DOOR_WIDTH) return undefined;
  return { first, second, start, end };
}

/** Locates the shared overlap in either participating wall's directed coordinates. */
function connectionRange(connection: DoorConnection, wall: WallSegment): { start: number; end: number } {
  if (wall === connection.first) return { start: connection.start, end: connection.end };
  const first = connection.first;
  const firstPoint = { x: first.start.x + first.ux * connection.start, y: first.start.y + first.uy * connection.start };
  const lastPoint = { x: first.start.x + first.ux * connection.end, y: first.start.y + first.uy * connection.end };
  const start = distanceOnWall(wall, firstPoint);
  const end = distanceOnWall(wall, lastPoint);
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

/** Places a door near a suitable end while keeping its full width within the shared boundary. */
function automaticDoorOffset(connection: DoorConnection, wall: WallSegment): number {
  const range = connectionRange(connection, wall);
  const walls = wall.room.walls;
  const index = walls?.indexOf(wall.wall) ?? -1;
  const previous = index >= 0 && walls ? walls.at((index + walls.size - 1) % walls.size) : undefined;
  const preferStart = previous ? !wall.room.isExteriorSegment(previous) : true;
  const edgeDistance = DOOR_WIDTH / 2 + DOOR_EDGE_DISTANCE;
  const center = clamp(
    preferStart ? range.start + edgeDistance : range.end - edgeDistance,
    range.start + DOOR_WIDTH / 2,
    range.end - DOOR_WIDTH / 2,
  );
  return center / wall.length;
}

/** Generates segment-bound data; persistent IDs are assigned when the room creates its opening group. */
export function generateAutoOpenings(activeFloor: C1_Floor): C1_AutoOpening[] {
  const openings: C1_AutoOpening[] = [];
  const segments = floorWallSegments(activeFloor);
  const entrances = new Map<string, WallSegment>();
  if (activeFloor.level === 0) {
    for (const wall of segments) {
      if (wall.room.type !== RoomType.Hallway || !wall.exterior || wall.length < HALLWAY_ENTRANCE_DOOR_WIDTH) continue;
      const existing = entrances.get(wall.room.id);
      if (!existing || wall.length < existing.length) entrances.set(wall.room.id, wall);
    }
  }

  const roomsWithDoor = new Set<string>();
  for (const wall of segments) {
    if (!wall.exterior) continue;
    const entrance = entrances.get(wall.room.id) === wall;
    if (!entrance && wall.length < WINDOW_WIDTH) continue;
    openings.push({
      kind: entrance ? "door" : "window",
      mode: "auto",
      ...(entrance ? { doorType: "entrance" as const, reflect: false } : {}),
      roomId: wall.room.id,
      _wallId: wall.wall.id,
      side: wall.side,
      width: entrance ? HALLWAY_ENTRANCE_DOOR_WIDTH : WINDOW_WIDTH,
      offset: 0.5,
    });
    if (entrance) roomsWithDoor.add(wall.room.id);
  }

  const connections: DoorConnection[] = [];
  for (let firstIndex = 0; firstIndex < segments.length; firstIndex += 1) {
    for (let secondIndex = firstIndex + 1; secondIndex < segments.length; secondIndex += 1) {
      const connection = sharedWallConnection(segments[firstIndex], segments[secondIndex]);
      if (connection) connections.push(connection);
    }
  }

  // Prefer a direct hallway connection for each room before considering other neighbors.
  for (const connection of connections) {
    const firstHallway = connection.first.room.type === RoomType.Hallway;
    const secondHallway = connection.second.room.type === RoomType.Hallway;
    if (firstHallway === secondHallway) continue;
    const wall = firstHallway ? connection.second : connection.first;
    if (roomsWithDoor.has(wall.room.id)) continue;
    addConnectingDoor(openings, roomsWithDoor, connection, wall);
  }
  for (const connection of connections) {
    const firstHasDoor = roomsWithDoor.has(connection.first.room.id);
    const secondHasDoor = roomsWithDoor.has(connection.second.room.id);
    if (firstHasDoor && secondHasDoor) continue;
    const useSecond = firstHasDoor || (!secondHasDoor && connection.second.length < connection.first.length);
    addConnectingDoor(openings, roomsWithDoor, connection, useSecond ? connection.second : connection.first);
  }
  return openings;
}

/** Adds one physical door and marks both connected rooms as having access. */
function addConnectingDoor(
  openings: C1_AutoOpening[],
  roomsWithDoor: Set<string>,
  connection: DoorConnection,
  wall: WallSegment,
): void {
  const offset = automaticDoorOffset(connection, wall);
  openings.push({
    kind: "door",
    mode: "auto",
    doorType: "interior",
    roomId: wall.room.id,
    _wallId: wall.wall.id,
    side: wall.side,
    width: DOOR_WIDTH,
    offset,
    reflect: doorReflectForWallOffset(offset),
  });
  roomsWithDoor.add(connection.first.room.id);
  roomsWithDoor.add(connection.second.room.id);
}
